import "./monthpicker.scss";
import React from "react";
import { type Moment } from "moment";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Buttons/Button";
import MonthPickerYear from "./MonthPickerYear";

type Props = {
    mobile: boolean;
    range?: boolean;
    start: Moment | null;
    end: Moment | null;
    viewing: Moment;
    onClick: (value: Moment) => void;
    onView: (viewing: Moment) => void;
};

type State = {
    animate: boolean;
    direction: "left" | "right" | null;
    next: Moment;
};

export default class MonthPicker extends React.Component<Props, State> {
    public _animating: ReturnType<typeof setTimeout>;

    constructor(props: Readonly<Props>) {
        super(props);

        this.state = {
            animate  : false,
            direction: null,
            next     : props.viewing.clone().add(1, "y"),
        };
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.viewing !== this.props.viewing) {
            this.setState({
                next: this.props.viewing.clone().add(1, "y"),
            });
        }
    }

    public componentWillUnmount(): void {
        if(this._animating) {
            clearTimeout(this._animating);
        }
    }

    public animateChange = (direction: "left" | "right"): void => {
        if(this.state.animate) {
            return;
        }

        this.setState({
            animate: true,
            direction,
        }, () => {
            this._animating = setTimeout(() => {
                if(direction === "left") {
                    this.props.onView(this.props.viewing.clone().subtract(1, "y"));
                } else {
                    this.props.onView(this.state.next);
                }

                this.setState({
                    animate  : false,
                    direction: null,
                });
            }, 300);
        });
    };

    public handleMonthPrevious = (): void => {
        this.animateChange("left");
    };

    public handleMonthNext = (): void => {
        this.animateChange("right");
    };

    public renderAnimating = (right: boolean): React.ReactNode => {
        if(this.state.animate) {
            if(right && this.state.direction === "right") {
                if(this.props.mobile) {
                    return (
                        <MonthPickerYear
                            year={this.state.next}
                            range={!!this.props.range}
                            start={this.props.start}
                            end={this.props.end}
                            onClick={this.props.onClick}
                        />
                    );
                }

                const after = this.state.next.clone().add(1, "y");

                return (
                    <MonthPickerYear
                        year={after}
                        range={!!this.props.range}
                        start={this.props.start}
                        end={this.props.end}
                        onClick={this.props.onClick}
                    />
                );
            } else if(!right && this.state.direction === "left") {
                const before = this.props.viewing.clone().subtract(1, "y");

                return (
                    <MonthPickerYear
                        year={before}
                        range={!!this.props.range}
                        start={this.props.start}
                        end={this.props.end}
                        onClick={this.props.onClick}
                    />
                );
            }
        }

        return (
            <div className="calendar-animate-shell" />
        );
    };

    public render(): React.ReactNode {
        const classes = [ "calendar-month-picker-years" ];

        if(this.state.animate && this.state.direction) {
            classes.push("animating", this.state.direction);
        }

        return (
            <div className="calendar-month-picker">
                <div className="calendar-month-picker-header">
                    <Button
                        type="button"
                        classes={[ "calendar-year-change previous" ]}
                        onClick={this.handleMonthPrevious}
                    >
                        <FontAwesomeLight icon="angle-left" />
                    </Button>
                    <Button
                        type="button"
                        classes={[ "calendar-year-change next" ]}
                        onClick={this.handleMonthNext}
                    >
                        <FontAwesomeLight icon="angle-right" />
                    </Button>
                </div>
                <div className={classes.join(" ")}>
                    {this.renderAnimating(false)}
                    <MonthPickerYear
                        year={this.props.viewing}
                        range={!!this.props.range}
                        start={this.props.start}
                        end={this.props.end}
                        onClick={this.props.onClick}
                    />
                    {!this.props.mobile && (
                        <MonthPickerYear
                            year={this.state.next}
                            range={!!this.props.range}
                            start={this.props.start}
                            end={this.props.end}
                            onClick={this.props.onClick}
                        />
                    )}
                    {this.renderAnimating(true)}
                </div>
            </div>
        );
    }
}
